import React from "react";
import styles from "./LandingPage.module.css";

export default function CftcSummaryValueSection({
  onCtaClick,
}: {
  onCtaClick?: () => void;
}) {
  return (
    <section id="cftc-summary" className={`${styles.section} ${styles.cftcSummarySection}`}>
      <div className={styles.sectionInner}>
        <div className={styles.cftcSummaryHeader}>
          <h2 className={styles.sectionTitle}>How We Read the CFTC — Step by Step</h2>
          <p className={styles.sectionSubtitle}>
            From contract selection to participant proof. No predictions. No narratives. Just
            positioning, normalized and auditable.
          </p>
        </div>

        <div className={styles.cftcSummaryGrid}>
          <div className={styles.cftcSummaryPreview}>
            <div className={styles.cftcSummaryPreviewTop}>
              <span className={styles.cftcSummaryPill}>Contract: 6E (Euro FX)</span>
              <span className={styles.cftcSummaryPillMuted}>Weekly</span>
            </div>
            <div className={styles.cftcSummaryPreviewGrid}>
              <div className={styles.cftcSummaryGhostCard}>
                <span>Open Interest Ownership</span>
              </div>
              <div className={styles.cftcSummaryGhostCard}>
                <span>Position Snapshot</span>
              </div>
              <div className={styles.cftcSummaryGhostCard}>
                <span>% of OI (Crowding)</span>
              </div>
              <div className={styles.cftcSummaryGhostCard}>
                <span>Change-From (Weekly Shift)</span>
              </div>
            </div>
            <div className={styles.cftcSummaryProofRow}>
              Participant Table (Audit)
            </div>
          </div>

          <div className={styles.cftcSummarySteps}>
            <div className={styles.cftcSummaryStepCard}>
              <h3>Start with the Market</h3>
              <p>
                Get the contract right — or everything else is noise. We anchor every analysis to
                the exact futures contract. Wrong contract equals wrong open interest, wrong
                participants, wrong conclusions.
              </p>
            </div>
            <div className={styles.cftcSummaryStepCard}>
              <h3>See Who Controls the Open Interest</h3>
              <p>
                Influence matters more than direction. The ownership distribution shows which
                participant groups are actually controlling the market’s open interest — Dealers,
                Asset Managers, or Leveraged Funds.
              </p>
            </div>
            <div className={styles.cftcSummaryStepCard}>
              <h3>Reveal the Positioning Bias</h3>
              <p>
                The posture behind price. The Position Snapshot exposes gross long and short
                exposure across participant groups — the raw positioning that price action alone
                can’t explain.
              </p>
            </div>
            <div className={styles.cftcSummaryStepCard}>
              <h3>Normalize for Regime</h3>
              <p>
                Crowding without distortion. We normalize positioning as a % of Open Interest to
                surface crowding pressure and regime extremes that are actually comparable across
                time.
              </p>
            </div>
            <div className={styles.cftcSummaryStepCard}>
              <h3>Track the Weekly Shift</h3>
              <p>
                Who’s adding, who’s covering — and who’s early. Change-from charts reveal weekly
                positioning flow and whether the move is structural or temporary.
              </p>
            </div>
            <div className={styles.cftcSummaryStepCard}>
              <h3>Audit the Data</h3>
              <p>
                Nothing is hidden. Everything is verifiable. Every chart ties back to the
                participant table — the full report, organized for validation, journaling, and
                review.
              </p>
            </div>

            <div className={styles.cftcSummaryGuardrails}>
              <h4>What this is / what this isn’t</h4>
              <ul>
                <li>✅ Institutional positioning context</li>
                <li>✅ Normalized crowding + weekly flow</li>
                <li>✅ Transparent, auditable proof layer</li>
                <li>❌ Not price prediction</li>
                <li>❌ Not trade signals</li>
                <li>❌ Not execution</li>
              </ul>
            </div>

            <div className={styles.cftcSummaryCtaRow}>
              <button className={styles.ctaPrimary} onClick={onCtaClick}>
                View the CFTC Summary
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
